<template>
	<Teleport to="#tooltips-container">
		<div
			v-if="show"
			ref="menuRef"
			class="
				absolute top-0 left-0 z-9999 w-25 rounded bg-white py-2 text-base
				text-won-dark-blue shadow-radio-card
			"
			:style="menuPositionStyles"
		>
			<div
				class="flex cursor-pointer items-center gap-2 px-3 py-1 hover:bg-won-gray-light"
				@click="cutHandler"
			>
				<ScissorsIcon class="size-4 text-won-dark-blue" />
				<span>Cut</span>
			</div>

			<div
				class="flex cursor-pointer items-center gap-2 px-3 py-1 hover:bg-won-gray-light"
				@click="copyHandler"
			>
				<ClipboardIcon class="size-4 text-won-dark-blue" />
				<span>Copy</span>
			</div>

			<div
				class="flex cursor-pointer items-center gap-2 px-3 py-1 hover:bg-won-gray-light"
				@click="pasteHandler"
			>
				<DocumentDuplicateIcon class="size-4 text-won-dark-blue" />
				<span>Paste</span>
			</div>

			<div
				class="flex cursor-pointer items-center gap-2 px-3 py-1 hover:bg-won-gray-light"
				@click="deleteHandler"
			>
				<TrashIcon class="size-4 text-won-dark-blue" />
				<span>Delete</span>
			</div>
		</div>
	</Teleport>
</template>

<script setup>
import { ref, toRefs, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { onClickOutside } from '@vueuse/core';

import {
	ClipboardIcon,
	TrashIcon,
	DocumentDuplicateIcon,
	ScissorsIcon
} from '@heroicons/vue/24/solid';
import { useMousePosition } from '../../store/mouse-position.store';
import { useTiptapStore } from '../../store/tiptap.store';
import { useNotifications } from '../../store/notifications.store';


const props = defineProps({
	show: {
		type: Boolean,
		default: false
	},
	hideContextMenu: {
		type: Function,
		default: () => {}
	}
});
const { show, hideContextMenu } = toRefs(props);

const mousePositionStore = useMousePosition();
const { posX, posY } = storeToRefs(mousePositionStore);

const tiptapStore = useTiptapStore();
const { editor } = storeToRefs(tiptapStore);
const notificationsStore = useNotifications();

const menuRef = ref(null);

onClickOutside(menuRef, hideContextMenu.value);

const menuPositionStyles = computed(() => {
	return { transform: `translate(${posX.value}px, ${posY.value}px)` };
});

const copyHandler = () => {
	editor.value.chain().focus().copy().run();
	hideContextMenu.value();
};

const cutHandler = async () => {
	editor.value.chain().focus().cut().run();
	hideContextMenu.value();
};

const showPasteFallbackNotice = () => {
	const isMac = navigator.platform?.toUpperCase().includes('MAC');
	const shortcut = isMac ? '⌘+V' : 'Ctrl+V';
	notificationsStore.notify(`Unable to paste. Please use ${shortcut} instead.`);
};

const pasteHandler = async () => {
	if (typeof navigator.clipboard?.readText !== 'function') {
		showPasteFallbackNotice();
		hideContextMenu.value();
		return;
	}

	try {
		await editor.value.chain().focus().paste().run();
	} catch {
		showPasteFallbackNotice();
	}
	hideContextMenu.value();
};

const deleteHandler = () => {
	editor.value.commands.deleteSelection();
	hideContextMenu.value();
};
</script>
